import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import {
  createFileRoute,
  redirect,
  useLoaderData,
  useRouter,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect, type JSX } from "react";
import { z } from "zod";
import { useResetAtom } from "jotai/utils";
import { appointmentFormAtom } from "@/lib/atoms/appointment-form-atom";
import { ensureQueryData } from "@/hooks/use-graphql";
import { Page } from "@/components/ui/layout/page/page";
import {
  HeaderRoot,
  HeaderButton,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { PatientSwapPatientList } from "@/components/patient/swap/patient-swap-patient-list";
import { zonedDate, getTimeDateFromStrings } from "@/lib/helpers/date";

const PatientSwapSearchPageQuery = graphql(/* GraphQL */ `
  query PatientSwapSearchPageQuery($codAgendamento: Int!) {
    appointment: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codAgendamento
      data
      horaInicio
      horaFim
    }

    ...PatientSwapPatientListFragment
  }
`);

const PatientSwapSearchPage = (): JSX.Element => {
  const { data } = useLoaderData({
    from: "/appointment/$appointmentId/patient-swap-search",
  });

  const { scope, count, quotaCount } = Route.useSearch();

  const router = useRouter();

  const setAtomValues = useSetAtom(appointmentFormAtom);

  const resetAppointmentFormAtom = useResetAtom(appointmentFormAtom);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (data) {
      const { appointment } = data;

      const appointmentDate = appointment?.data ? zonedDate(appointment.data) : undefined;

      setAtomValues((prev) => ({
        ...prev,
        appointmentDate,
        appointmentTime: {
          start: appointment?.horaInicio
            ? getTimeDateFromStrings(
                appointmentDate ?? new Date(),
                appointment.horaInicio,
              )
            : new Date(),
          end: appointment?.horaFim
            ? getTimeDateFromStrings(appointmentDate ?? new Date(), appointment.horaFim)
            : new Date(),
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Deve ser executado somente no carregamento da página
  }, []);

  const isPending = !data?.appointment;

  const onBackButtonClick = (): void => {
    resetAppointmentFormAtom();
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton onClick={onBackButtonClick} icon={Left} align="start" />
        <HeaderTitle title="Trocar paciente" align="center" />
      </HeaderRoot>

      <Page>
        {isPending ? (
          <FullPageSpinner />
        ) : (
          <PatientSwapPatientList
            data={data}
            scope={scope}
            appointmentsToSwapCount={count}
            quotaAppointmentsToSwapCount={quotaCount}
          />
        )}
      </Page>
    </>
  );
};

const patientSwapSearchPageSearchSchema = z.object({
  scope: z.enum(["single", "all"]).catch("single"),
  count: z.number().optional(),
  quotaCount: z.number().optional(),
});

export const Route = createFileRoute("/appointment/$appointmentId/patient-swap-search")({
  component: PatientSwapSearchPage,
  validateSearch: patientSwapSearchPageSearchSchema,
  parseParams: (params) => ({
    appointmentId: z.int().parse(Number(params.appointmentId)),
  }),
  loader: async (opts) => {
    try {
      const variables = {
        codAgendamento: opts.params.appointmentId,
      };

      const data = await ensureQueryData(
        opts.context,
        PatientSwapSearchPageQuery,
        variables,
      );

      const { appointment } = data;

      const appointmentNotFound = !appointment;

      if (appointmentNotFound) throw new Error("Appointment not found!");

      return { data };
    } catch (_e) {
      redirect({
        to: "/calendar",
        throw: true,
      });
    }

    return {};
  },
});
